import {z} from 'zod';

import {
  CheckoutProfileDraftSchema,
  CheckoutProfileWriteSchema,
  getCheckoutProfileDocumentCellKey,
  getCheckoutProfileDocumentCells,
  normalizeCheckoutProfileDraft,
} from '@dms/api/sales';
import i18n from '@dms/i18n';

import {getCheckoutProfileMissingRequiredFields} from '../utils/getCheckoutProfileMissingRequiredFields';
import {
  CheckoutProfileFormValuesSchema,
  type CheckoutProfileFormValues,
} from './CheckoutProfileFormValuesSchema';

// Zod skips the transform when the object rules fail, so the sections the editor hides (a disabled
// deposit, the external settings of an internal sale) are stripped before those rules run. The
// documents keep their form order, so the index mapping of the transform still applies.
const stripHiddenSections = (values: unknown) => {
  const draft = CheckoutProfileDraftSchema.safeParse(values);

  return draft.success
    ? {...normalizeCheckoutProfileDraft(draft.data), documents: draft.data.documents}
    : values;
};

// The backend distinguishes a missing document row from an explicit unrestricted row.
export const CheckoutProfileFormSchema = z
  .preprocess(stripHiddenSections, CheckoutProfileFormValuesSchema)
  .transform((profile, context) => {
    const normalized = normalizeCheckoutProfileDraft(profile);
    const configured = new Set(normalized.documents.map(getCheckoutProfileDocumentCellKey));
    const documents: CheckoutProfileFormValues['documents'] = [
      ...normalized.documents,
      ...getCheckoutProfileDocumentCells(normalized)
        .filter((cell) => !configured.has(getCheckoutProfileDocumentCellKey(cell)))
        .map((cell) => ({
          ...cell,
          allowedSeriesIds: [],
          allowedTemplateIds: [],
          isOverrideAllowed: false,
        })),
    ];

    getCheckoutProfileMissingRequiredFields(normalized).forEach((path) => {
      context.addIssue({
        code: 'custom',
        path,
        message: i18n.t('general.validations.fieldIsRequired'),
      });
    });

    profile.documents.forEach((row, index) => {
      if (!configured.has(getCheckoutProfileDocumentCellKey(row))) {
        return;
      }

      if (row.restrictSeries && row.allowedSeriesIds.length === 0) {
        context.addIssue({
          code: 'custom',
          path: ['documents', index, 'allowedSeriesIds'],
          message: i18n.t('general.validations.fieldIsRequired'),
        });
      }

      if (row.restrictTemplates && row.allowedTemplateIds.length === 0) {
        context.addIssue({
          code: 'custom',
          path: ['documents', index, 'allowedTemplateIds'],
          message: i18n.t('general.validations.fieldIsRequired'),
        });
      }
    });

    const parsed = CheckoutProfileWriteSchema.safeParse({
      ...normalized,
      documents: documents.map((row) => ({
        ...row,
        defaultSeriesId: row.defaultSeriesId ?? null,
        defaultTemplateId: row.defaultTemplateId ?? null,
      })),
    });

    if (!parsed.success) {
      // Normalization removes disabled rows; errors must still target the form's original indices.
      const originalIndexes = profile.documents.flatMap((row, index) =>
        configured.has(getCheckoutProfileDocumentCellKey(row)) ? [index] : []
      );

      parsed.error.issues.forEach((issue) => {
        const [root, index, ...field] = issue.path;
        const originalIndex =
          root === 'documents' && typeof index === 'number' ? originalIndexes[index] : undefined;

        context.addIssue(
          originalIndex === undefined
            ? issue
            : {...issue, path: ['documents', originalIndex, ...field]}
        );
      });

      return z.NEVER;
    }

    return parsed.data;
  })
  .refine(
    (profile) =>
      !profile.foreignCurrency?.isAllowed ||
      !profile.foreignCurrency.isDefault ||
      !!profile.foreignCurrency.defaultCurrency,
    {
      path: ['foreignCurrency', 'defaultCurrency'],
      message: i18n.t('general.validations.fieldIsRequired'),
    }
  );
